import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { StatsigService } from '@statsig/angular-bindings';
import { StatsigUser } from '@statsig/js-client';
import { STATSIG_STATE } from '@fitness-force/state';

/**
 * Feature Flag Service for Statsig Integration
 *
 * This service provides a centralized, Angular-optimized interface for Statsig feature flags.
 * It includes caching, reactive updates, and proper error handling.
 *
 * ## Key Features:
 * - Centralized feature flag management
 * - Intelligent caching for performance
 * - Reactive observables for real-time updates
 * - NgRx state synchronization
 * - Error handling with fallbacks
 *
 * ## Usage:
 * ```typescript
 * constructor(private featureFlagService: FeatureFlagService) {}
 *
 * // Simple boolean check
 * const isEnabled = this.featureFlagService.isEnabled('my_feature');
 *
 * // Reactive updates
 * this.featureFlagService.getFlag$('my_feature').subscribe(enabled => {
 *   // Handle flag changes
 * });
 *
 * // Update user context
 * await this.featureFlagService.updateUser({ userID: '123', email: 'user@example.com' });
 * ```
 *
 * ## Migration Notes:
 * This service replaces direct `Statsig.checkGate()` calls throughout the application.
 * The migration eliminates performance issues caused by excessive API calls and provides
 * better Angular integration with observables and dependency injection.
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private readonly statsigService = inject(StatsigService);
  private readonly store = inject(Store);

  private readonly featureFlagCache = new Map<string, boolean>();
  private readonly flagSubjects = new Map<string, BehaviorSubject<boolean>>();
  private readonly initialized$ = new BehaviorSubject<boolean>(false);

  /**
   * Observable that emits when Statsig is fully initialized
   */
  public readonly isLoading$ = this.initialized$.pipe(
    map(initialized => !initialized),
    distinctUntilChanged(),
    shareReplay(1)
  );

  constructor() {
    // Initialize service asynchronously
    this.initializeService().catch(err => {
      console.error('Failed to initialize FeatureFlagService:', err);
      // Mark as initialized anyway to prevent hanging
      this.initialized$.next(true);
    });
    this.setupStateSync();
  }

  /**
   * Check if a feature flag is enabled (synchronous)
   * WARNING: This may return defaultValue if Statsig is not initialized yet.
   * For guaranteed results after page reload, use isEnabledAsync() instead.
   * @param flag The feature flag name
   * @param defaultValue Default value if flag is not found or service not initialized
   * @returns boolean indicating if the flag is enabled
   */
  isEnabled(flag: string, defaultValue = false): boolean {
    try {
      if (!this.initialized$.value) {
        return defaultValue;
      }

      // Return cached value to avoid repeated checkGate() calls which count
      // against Statsig's check rate. Cache is refreshed by cacheCommonFlags()
      // on initialization and on STATSIG_STATE changes.
      if (this.featureFlagCache.has(flag)) {
        return this.featureFlagCache.get(flag) ?? false;
      }

      const result = this.statsigService.checkGate(flag);
      this.featureFlagCache.set(flag, result);
      return result;
    } catch (error) {
      console.error(`Error checking feature flag ${flag}:`, error);
      return defaultValue;
    }
  }

  /**
   * Check if a feature flag is enabled (async - waits for initialization)
   * Use this method when you need guaranteed results after page reload.
   * @param flag The feature flag name
   * @param defaultValue Default value if flag is not found
   * @returns Promise<boolean> indicating if the flag is enabled
   */
  async isEnabledAsync(flag: string, defaultValue = false): Promise<boolean> {
    try {
      // Wait for Statsig to be fully initialized
      await this.waitForInitialization();
      
      // Now check the gate
      return this.isEnabled(flag, defaultValue);
    } catch (error) {
      console.error(`Error checking feature flag ${flag} async:`, error);
      return defaultValue;
    }
  }

  /**
   * Get an observable for a feature flag that emits on changes
   * This observable waits for Statsig initialization before emitting the first value.
   * Subsequent updates are pushed by setupStateSync() when STATSIG_STATE changes,
   * avoiding a per-flag store subscription that was causing excessive checkGate() calls.
   * @param flag The feature flag name
   * @param defaultValue Default value if flag is not found
   * @returns Observable<boolean> that emits the flag value
   */
  getFlag$(flag: string, defaultValue = false): Observable<boolean> {
    if (!this.flagSubjects.has(flag)) {
      const subject = new BehaviorSubject<boolean>(defaultValue);
      this.flagSubjects.set(flag, subject);

      // Emit the real value once Statsig is ready (calls checkGate once)
      this.waitForInitialization().then(() => {
        const currentValue = this.isEnabled(flag, defaultValue);
        subject.next(currentValue);
      });

      // State-change updates are handled centrally in setupStateSync() so we
      // do NOT create a per-flag store subscription here, which previously
      // caused one checkGate() call per flag per STATSIG_STATE emission.
    }

    return (this.flagSubjects.get(flag) ?? new BehaviorSubject<boolean>(defaultValue)).asObservable().pipe(
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  /**
   * Update the user context for Statsig
   * @param user The Statsig user object or null to clear user context
   * @returns Promise that resolves when user is updated
   */
  async updateUser(user: StatsigUser | null): Promise<void> {
    try {
      if (user === null) {
        // For logout, reset to anonymous user
        await this.statsigService.updateUserAsync({ userID: 'anonymous' });
      } else {
        await this.statsigService.updateUserAsync(user);
      }

      // Clear cache for commonly used flags to ensure fresh values
      this.clearCommonFlagsCache();

      // Update NgRx state (will be handled by effects)
    } catch (error) {
      console.error('Error updating Statsig user:', error);
      throw error;
    }
  }

  /**
   * Wait for Statsig initialization to complete
   * @returns Promise that resolves when Statsig is ready
   */
  waitForInitialization(): Promise<void> {
    return new Promise((resolve) => {
      // First ensure our service is initialized
      const waitForService = () => {
        if (this.initialized$.value) {
          let resolved = false;
          let statsigSubscription: any = null;
          
          // Then wait for Statsig's isLoading$ to be false
          statsigSubscription = this.statsigService.isLoading$.subscribe((isLoading) => {
            if (!isLoading && !resolved) {
              resolved = true;
              if (statsigSubscription) {
                statsigSubscription.unsubscribe();
              }
              resolve();
            }
          });
          
          // Timeout after 3 seconds
          setTimeout(() => {
            if (!resolved) {
              resolved = true;
              if (statsigSubscription) {
                statsigSubscription.unsubscribe();
              }
              resolve();
            }
          }, 3000);
        } else {
          let resolved = false;
          let subscription: any = null;
          
          subscription = this.initialized$.subscribe((initialized) => {
            if (initialized && !resolved) {
              resolved = true;
              if (subscription) {
                subscription.unsubscribe();
              }
              waitForService(); // Recursively wait for Statsig
            }
          });
          
          // Timeout after 3 seconds
          setTimeout(() => {
            if (!resolved) {
              resolved = true;
              if (subscription) {
                subscription.unsubscribe();
              }
              resolve();
            }
          }, 3000);
        }
      };
      
      waitForService();
    });
  }

  /**
   * Get cached flag value (for high-performance scenarios)
   * @param flag The feature flag name
   * @param defaultValue Default value if not cached
   * @returns Cached boolean value
   */
  getCachedFlag(flag: string, defaultValue = false): boolean {
    return this.featureFlagCache.get(flag) ?? defaultValue;
  }

  /**
   * Check if a feature flag is cached
   * @param flag The feature flag name
   * @returns boolean indicating if flag is cached
   */
  isFlagCached(flag: string): boolean {
    return this.featureFlagCache.has(flag);
  }

  /**
   * Manually refresh a specific flag's cache from Statsig and notify subscribers.
   * @param flag The feature flag name
   */
  refreshFlagCache(flag: string): void {
    try {
      // Call checkGate() directly (not isEnabled()) to bypass the cache read
      // and actually fetch a fresh value from Statsig.
      const value = this.statsigService.checkGate(flag);
      this.featureFlagCache.set(flag, value);

      const subject = this.flagSubjects.get(flag);
      if (subject) {
        subject.next(value);
      }
    } catch (error) {
      console.error(`Error refreshing flag cache for ${flag}:`, error);
    }
  }

  private async initializeService(): Promise<void> {
    try {
      // Wait a moment for the Statsig service to be available
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Mark as initialized - checkGate will handle errors if not ready yet
      this.initialized$.next(true);

      // Cache commonly used flags
      this.cacheCommonFlags();

    } catch (error) {
      console.error('Error initializing FeatureFlagService:', error);
      throw error;
    }
  }


  private setupStateSync(): void {
    // When the user context changes, refresh the cache and push updated values
    // to all active flag subjects so reactive consumers stay in sync.
    this.store.select(STATSIG_STATE).subscribe(state => {
      if (state?.user && this.initialized$.value) {
        this.cacheCommonFlags();
        this.pushCacheToFlagSubjects();
      }
    });
  }

  private cacheCommonFlags(): void {
    const commonFlags = [
      // Sidebar / navigation flags
      'show_dashboard',
      'show_campaign',
      'show_campaign_automation',
      'show_enterprise',
      'show_retail',
      'show_payoutengine',
      'show_payoutengine_provider',
      'show_corporate',
      'show_audit_trial',
      'show_upgrade',
      'show_subscription_amendment',
      'show_transfer',
      'show_adhoc_charges',
      'show_family_membership',
      'show_payengine_dispute_management',
      'show_payengine_transactions',
      'show_payengine_settlements',
      // Transaction / billing flags
      'show_refund',
      'show_salesreturn',
      'show_writeoff',
      'show_freeze_v2',
      'customer_credit_balance',
      'show_add_discount_on_billing_cycle',
      'show_addons_billing',
      'show_subscription_status_override',
      'show_stop_billing',
      // Client profile / grid row flags — resolved once per session, not per row
      'client_organisation_link',
      'show_delete_profile_option',
      'show_sms',
      'show_email',
      'show_digital_keyfob',
      'keyfob',
      // Staff / notification flags
      'show_staff_2way_message_notifciation',
      'show_create_or_manage_staff_availability',
      'show_commission_management',
      // Misc
      'staff_can_see_signup_link',
      // Admin settings page — all flags used in adminsettings.json must be pre-warmed
      // so hasFeatureFlagEnabled() can do a synchronous cache lookup
      'show_whatsapp',
      'show_push_notification',
      'show_family_memberships',
      'show_plan_change_management',
      'show_webhook',
      'show_widget',
      'show_gl_code_account',
      'facebook_custom_audience_sync',
      'bring_your_sms_provider',
      'show_charge_on_terminal_or_pos',
    ];

    // Call checkGate() directly here (not isEnabled()) so we always fetch
    // fresh values from Statsig rather than reading the stale cache we're
    // about to replace.
    commonFlags.forEach(flag => {
      try {
        const result = this.statsigService.checkGate(flag);
        this.featureFlagCache.set(flag, result);
      } catch {
        // Keep existing cached value on error
      }
    });
  }

  /**
   * Push current cache values to all active flag subjects so reactive
   * consumers receive updated values without needing their own store subscriptions.
   */
  private pushCacheToFlagSubjects(): void {
    this.flagSubjects.forEach((subject, flag) => {
      const cachedValue = this.featureFlagCache.get(flag);
      if (cachedValue !== undefined) {
        subject.next(cachedValue);
      }
    });
  }

  private clearCommonFlagsCache(): void {
    // Clear the entire cache on user change — all flags may now return
    // different values for the new user context.
    this.featureFlagCache.clear();
  }
}
